// Utility functions for handling API errors and making them user-friendly

interface ApiErrorResponse {
    errorCode?: string;
    ErrorCode?: string;
    message?: string;
    Message?: string;
    lockoutEndDate?: string;
    LockoutEndDate?: string;
}

function parseStructuredError(errorMessage: string): ApiErrorResponse | null {
    try {
        const parsed = JSON.parse(errorMessage) as ApiErrorResponse;
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        const wrappedJsonMatch = errorMessage.match(/\{[\s\S]*\}$/);
        if (!wrappedJsonMatch) {
            return null;
        }

        try {
            const parsed = JSON.parse(wrappedJsonMatch[0]) as ApiErrorResponse;
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    }
}

export function extractApiErrorCode(errorMessage: string): string | null {
    const errorData = parseStructuredError(errorMessage);
    if (!errorData) {
        return null;
    }

    const errorCode = errorData.errorCode || errorData.ErrorCode;
    return typeof errorCode === "string" && errorCode.trim() ? errorCode : null;
}

export function isAccountInactiveApiError(errorMessage: string): boolean {
    return extractApiErrorCode(errorMessage) === "ACCOUNT_INACTIVE";
}

/**
 * Parses API error responses and returns user-friendly error messages
 * @param errorMessage - The raw error message from the API
 * @returns A user-friendly error message
 */
export function parseApiError(errorMessage: string): string {
    const errorData = parseStructuredError(errorMessage);

    if (errorData) {
        const errorCode = errorData.errorCode || errorData.ErrorCode;
        const message = errorData.message || errorData.Message;
        const lockoutEndDate =
            errorData.lockoutEndDate || errorData.LockoutEndDate;

        // Handle specific error codes
        switch (errorCode) {
            case "ACCOUNT_LOCKED":
                if (lockoutEndDate) {
                    const lockoutDate = new Date(lockoutEndDate);
                    const formattedDate = lockoutDate.toLocaleDateString();
                    const formattedTime = lockoutDate.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                    });

                    return `Your account has been temporarily locked due to multiple failed login attempts. Please try again after ${formattedTime} on ${formattedDate}.`;
                } else if (message) {
                    // Extract date/time from the message if available
                    const dateMatch = message.match(
                        /(\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}:\d{2} (AM|PM))/
                    );
                    if (dateMatch) {
                        const lockoutDateTime = new Date(dateMatch[1]);
                        const formattedDate =
                            lockoutDateTime.toLocaleDateString();
                        const formattedTime =
                            lockoutDateTime.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                            });

                        return `Your account has been temporarily locked due to multiple failed login attempts. Please try again after ${formattedTime} on ${formattedDate}.`;
                    }
                }
                return "Your account has been temporarily locked due to multiple failed login attempts. Please try again later or contact your administrator for assistance.";

            case "INVALID_CREDENTIALS":
                return "The email or password you entered is incorrect. Please check your credentials and try again.";

            case "USER_NOT_FOUND":
                return "No account found with this email address. Please check your email or contact your administrator.";

            case "PASSWORD_EXPIRED":
                return "Your password has expired. Please contact your administrator to reset it.";

            case "ACCOUNT_DISABLED":
                return "Your account has been disabled. Please contact your administrator for assistance.";

            case "ACCOUNT_INACTIVE":
                return (
                    message ||
                    "Your account is inactive. Please contact your administrator."
                );

            default:
                // If there's a user-friendly message in the response, use it
                if (message && !message.includes("{")) {
                    return message;
                }
                break;
        }
    }

    // If it's not valid JSON, check if it's already a user-friendly message
    if (
        !errorMessage.includes("{") &&
        !errorMessage.includes("errorCode") &&
        errorMessage.length < 200
    ) {
        return errorMessage;
    }

    // Default fallback for any unhandled errors
    return "An error occurred while trying to log in. Please check your credentials and try again, or contact your administrator if the problem persists.";
}

/**
 * Checks if an error message contains technical/JSON data
 * @param errorMessage - The error message to check
 * @returns True if the message appears to be technical/JSON
 */
export function isTechnicalError(errorMessage: string): boolean {
    return (
        errorMessage.includes("{") ||
        errorMessage.includes("errorCode") ||
        errorMessage.includes("HTTP") ||
        errorMessage.startsWith("Error:")
    );
}

/**
 * Extracts a user-readable message from an unknown error so toasts/UI never show raw JSON.
 * Looks for `.message` (or `.Message`) inside ApiError.bodyText OR inside error.message itself
 * when those fields are JSON strings. If neither yields a friendly message, returns the
 * fallback — raw Error.message is never surfaced because it commonly contains technical text.
 */
export function extractApiErrorMessage(
    error: unknown,
    fallback: string
): string {
    const tryExtract = (text: string | undefined | null): string | null => {
        if (!text || typeof text !== "string") return null;
        try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === "object") {
                const candidate =
                    (parsed as { message?: unknown }).message ??
                    (parsed as { Message?: unknown }).Message;
                if (
                    typeof candidate === "string" &&
                    candidate.trim().length > 0
                ) {
                    return candidate;
                }
            }
        } catch {
            // not JSON — fall through
        }
        return null;
    };

    if (error instanceof Error) {
        const fromBody = tryExtract((error as { bodyText?: string }).bodyText);
        if (fromBody) return fromBody;

        const fromMessage = tryExtract(error.message);
        if (fromMessage) return fromMessage;

        return fallback;
    }

    if (typeof error === "string") {
        const fromString = tryExtract(error);
        if (fromString) return fromString;
        return fallback;
    }

    return fallback;
}
